import React, { useState, useEffect } from 'react';
import { getOperators, createOperator, deleteOperator } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, UserCog, Hash } from 'lucide-react';

export function OperatorsManager() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadOperators(); }, []);

  const loadOperators = async () => {
    try {
      const res = await getOperators();
      setOperators(res.data || []);
    } catch { toast.error('Failed to load operators'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newCode.trim() || !newName.trim()) {
      toast.error('Both code and name are required');
      return;
    }
    setAdding(true);
    try {
      await createOperator({ code: newCode.trim(), name: newName.trim() });
      toast.success('Operator added');
      setNewCode('');
      setNewName('');
      loadOperators();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add operator');
    } finally { setAdding(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove operator "${name}"?`)) return;
    try {
      await deleteOperator(id);
      toast.success('Operator removed');
      loadOperators();
    } catch { toast.error('Failed to remove operator'); }
  };

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="bg-card border rounded-lg p-5 space-y-4" data-testid="operators-manager">
      <div className="flex items-center gap-2 mb-1">
        <UserCog className="w-5 h-5 text-primary" />
        <h3 className="text-base font-semibold">Operators</h3>
        <span className="text-xs text-muted-foreground ml-auto">{operators.length} operator{operators.length !== 1 ? 's' : ''}</span>
      </div>
      <p className="text-sm text-muted-foreground">
        Manage operator codes for the Quick Order (Operator View). Each operator enters their code to log who submitted the order.
      </p>

      {/* Add new operator */}
      <div className="flex gap-2 items-end">
        <div className="space-y-1 w-32">
          <Label className="text-xs">Code</Label>
          <Input
            value={newCode}
            onChange={e => setNewCode(e.target.value.toUpperCase())}
            placeholder="e.g. OP01"
            className="h-9 font-mono"
            maxLength={10}
            data-testid="input-operator-code"
          />
        </div>
        <div className="space-y-1 flex-1">
          <Label className="text-xs">Name</Label>
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Operator name"
            className="h-9"
            data-testid="input-operator-name"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
        </div>
        <Button onClick={handleAdd} disabled={adding} size="sm" className="h-9" data-testid="btn-add-operator">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>

      {/* Operators list */}
      {operators.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-4 text-center">No operators defined yet. Add one above.</p>
      ) : (
        <div className="space-y-1">
          {operators.map(op => (
            <div key={op.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 group hover:bg-muted/60 transition-colors" data-testid={`operator-${op.code}`}>
              <div className="flex items-center justify-center w-12 h-8 bg-primary/10 rounded text-xs font-mono font-bold text-primary">
                {op.code}
              </div>
              <span className="flex-1 text-sm font-medium">{op.name}</span>
              <Button
                variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:text-destructive"
                onClick={() => handleDelete(op.id, op.name)}
                data-testid={`btn-delete-operator-${op.code}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
